import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Form, Button, FormGroup, Label, Input, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import moment from "moment";

function ComprasCreate() {
  const { user } = useContext(AuthContext);
  const URL_COMPRAS            = process.env.REACT_APP_URL_COMPRAS;
  const URL_COMPRAS_ACUMULADOS = process.env.REACT_APP_URL_COMPRAS_ACUMULADOS;
  const URL_INVENTARIOS        = process.env.REACT_APP_URL_INVENTARIOS;
  const URL_AREAS              = process.env.REACT_APP_URL_AREAS;
  const URL_ARTICULOS_OC       = process.env.REACT_APP_URL_ARTICULOS_OC;
  const URL_ARL                = process.env.REACT_APP_URL_ARTICULOS_RECEPCION_LAREDO;
  const URL_LOGS               = process.env.REACT_APP_URL_LOGS;
  const URL_VARIANTES          = process.env.REACT_APP_URL_VARIANTES_ARTICULOS;

  const auth = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };

  // rl = RecepcionLaredo ID (si viene de Laredo); si no, flujo directo de OC
  const rl = new URLSearchParams(window.location.search).get("rl");

  // Almacenes permitidos en Entradas — 10 áreas exactas por ID
  const AREAS_ENTRADA_IDS = new Set([
    process.env.REACT_APP_AREAS_CEDIS,           // CEDIS (Celmix)
    process.env.REACT_APP_AREAS_LOGISPRO,         // LOGISPRO
    process.env.REACT_APP_AREAS_AMAZON_1_FBA,     // Amazon 1 FBA
    process.env.REACT_APP_AREAS_AMAZON_2_FBA,     // Amazon 2 FBA
    process.env.REACT_APP_AREAS_WALMART_WFS,      // Walmart WFS
    process.env.REACT_APP_AREAS_MELI_1_FULL,      // MercadoLibre FULL Celmix
    process.env.REACT_APP_AREAS_MELI_2_FULL,      // MercadoLibre FULL MICROOP
    process.env.REACT_APP_AREAS_DEVOLUCIONES,     // Devoluciones
    process.env.REACT_APP_AREAS_TECNICO,          // Técnico
    process.env.REACT_APP_AREAS_MERMAS,           // Mermas
    process.env.REACT_APP_AREAS_PERDIDAS,         // Pérdidas
  ].filter(Boolean));
  const areaPermitida = (a) => AREAS_ENTRADA_IDS.has(a._id);

  const [fecha, setFecha]               = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("NA");
  const [selectedArea, setSelectedArea] = useState("");
  const [areas, setAreas]               = useState([]);
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    const loadAreas = axios.get(URL_AREAS, auth);

    if (rl) {
      // Modo Laredo: carga artículos de esa recepción Laredo
      Promise.all([
        axios.get(`${URL_ARL}/recepcion/${rl}`, auth),
        loadAreas,
      ]).then(async ([{ data: laredoItems }, { data: areasData }]) => {
        setAreas(areasData);
        const pendientes = laredoItems.filter(a => (a.pendiente_recibir ?? a.cantidad) > 0);
        const mapped = pendientes.map(a => {
          const art = a.articulos[0] || {};
          const rlDoc = a.recepcionLaredo[0] || {};
          const pendRecibir = a.pendiente_recibir ?? a.cantidad;
          return {
            _id: a._id,
            idArticulo: art._id,
            nombre: art.nombre,
            mlm: art.MLM || art.SKU || "—",
            pendiente_recibir: pendRecibir,
            cantidadRecibir: pendRecibir,
            precio: a.precio,
            tipoCambio: rlDoc.tipoCambio || 0,
            gastosEnvio: rlDoc.gastosEnvio || 0,
            gastosImportacion: rlDoc.gastosImportacion || 0,
            noFactura: rlDoc.noFactura || "",
            lote: "NA",
            checked: true,
            variante: a.variante?._id || a.variante || "",
            varianteNombre: a.variante?.nombre || "Sin variante",
            variantesOptions: [],  // solo lectura en modo Laredo
          };
        });
        setItems(mapped);
      }).catch(err => console.log(err))
        .finally(() => setLoading(false));
    } else {
      // Modo OC: carga artículos pendientes de embarcar desde OC
      Promise.all([
        axios.get(`${URL_ARTICULOS_OC}/PendEmbarcar`, auth),
        loadAreas,
      ]).then(async ([{ data: pendientes }, { data: areasData }]) => {
        setAreas(areasData);
        const uniqueArticulos = [...new Set(pendientes.map(a => a.idArticulo).filter(Boolean))];
        const variantesMap = {};
        await Promise.all(
          uniqueArticulos.map(id =>
            axios.get(`${URL_VARIANTES}/articulo/${id}`, auth)
              .then(r => { variantesMap[id] = r.data; })
              .catch(() => { variantesMap[id] = []; })
          )
        );
        const mapped = pendientes.map(a => ({
          ...a,
          pendiente_recibir: a.pendiente_embarcar,
          cantidadRecibir: a.pendiente_embarcar,
          lote: "NA",
          checked: true,
          variante: a.idVariante || "",
          variantesOptions: variantesMap[a.idArticulo] || [],
        }));
        setItems(mapped);
      }).catch(err => console.log(err))
        .finally(() => setLoading(false));
    }
  }, []);

  function updateItem(id, field, val) {
    setItems(prev => prev.map(i => i._id === id ? { ...i, [field]: val } : i));
  }

  const total_cantidad = items
    .filter(i => i.checked)
    .reduce((s, i) => s + (parseFloat(i.cantidadRecibir) || 0), 0);

  async function save(e) {
    e.preventDefault();
    if (!selectedArea) { Swal.fire("Error", "Selecciona un almacén", "error"); return; }
    const toSave = items.filter(i => i.checked && parseFloat(i.cantidadRecibir) > 0);
    if (!toSave.length) { Swal.fire("Error", "No hay artículos a recibir", "error"); return; }

    setSaving(true);
    try {
      const compraBody = {
        fecha,
        tipoCambio: toSave[0]?.tipoCambio || 0,
        observaciones,
        total_cantidad,
        user: user.id,
        areas: selectedArea,
      };
      if (rl) compraBody.recepcionLaredo = rl;

      const { data: compra } = await axios.post(URL_COMPRAS, compraBody, auth);

      const totalPiezasEntrada = toSave.reduce((s, i) => s + (parseFloat(i.cantidadRecibir) || 0), 0);
      for (const item of toSave) {
        const cant = parseFloat(item.cantidadRecibir);
        const tc   = parseFloat(item.tipoCambio) || 0;
        const gastosEnvio = parseFloat(item.gastosEnvio) || 0;
        const gastosImportacion = parseFloat(item.gastosImportacion) || 0;
        const costoUnitario = item.precio * tc
          + (totalPiezasEntrada > 0 ? (gastosEnvio + gastosImportacion * tc) / totalPiezasEntrada : 0);

        // Entrada a inventario
        await axios.patch(`${URL_INVENTARIOS}Compra`, {
          articulos: item.idArticulo,
          cantidad: cant,
          areas: selectedArea,
          precioMX: item.precio * tc,
          costoUnitario,
          lote: item.lote,
          variante: item.variante || null,
        }, auth);

        if (rl) {
          // Modo Laredo: marca recibido en ArticulosRecepcionLaredo
          await axios.patch(`${URL_ARL}Recibido/${item._id}`, { recibido: cant }, auth);
        } else {
          // Modo OC directo: actualiza embarcado en ArticulosOC
          await axios.patch(`${URL_ARTICULOS_OC}editEmbarcado/${item._id}`, {
            embarcado: cant,
            ordenesCompra: item.ocId,
          }, auth);
        }

        await axios.post(URL_COMPRAS_ACUMULADOS, {
          fecha,
          cantidad: cant,
          compras: compra._id,
          articulos: item.idArticulo,
          areas: selectedArea,
          variante: item.variante || null,
          precio: item.precio,
          tipoCambio: tc,
          precioMX: item.precio * tc,
        }, auth);
      }

      await axios.post(URL_LOGS, { tipo: "Crear Compra", detalle: rl ? "Entrada desde Bodega USA" : "Entrada desde OC", user: user.id }, auth);
      Swal.fire({ icon: "success", title: "Entrada creada", timer: 1500, showConfirmButton: false });
      setTimeout(() => (window.location.href = "/ListadoCompras"), 1600);
    } catch (err) {
      console.log(err);
      Swal.fire("Error", "No se pudo guardar la entrada", "error");
      setSaving(false);
    }
  }

  const sec = { borderLeft: "4px solid #3b82f6", paddingLeft: 12, marginBottom: 8, color: "#1e3a8a", fontWeight: 600, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em" };
  const block = { background: "#f8fafc", borderRadius: 8, padding: "14px 16px", marginBottom: 16 };

  return (
    <>
      <Header />
      <br /><br />
      {user.menu_compras ? (
        <div className="card container col-12">
          <h3 className="text-center mt-3 mb-3">Nueva Entrada</h3>
          <Form onSubmit={save}>

            {/* 📋 Encabezado */}
            <div style={block}>
              <div style={sec}>📋 Encabezado</div>
              <Row>
                <Col md={2}>
                  <FormGroup>
                    <Label>Fecha</Label>
                    <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)} required />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Almacén Destino</Label>
                    <Input type="select" value={selectedArea}
                      onChange={e => setSelectedArea(e.target.value)} required>
                      <option value="">Selecciona un almacén</option>
                      {areas.filter(areaPermitida).sort((a, b) => a.name > b.name ? 1 : -1).map(a => (
                        <option key={a._id} value={a._id}>{a.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={5}>
                  <FormGroup>
                    <Label>Observaciones</Label>
                    <Input type="text" value={observaciones}
                      onChange={e => setObservaciones(e.target.value)} required />
                  </FormGroup>
                </Col>
              </Row>
            </div>

            {/* 📦 Artículos */}
            <div style={block}>
              <div style={{ ...sec, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  {rl
                    ? "🚛 Artículos de Recepción Bodega USA"
                    : "📦 Artículos Pendientes de Recibir (OC)"}
                </span>
                {!loading && items.length > 0 && (
                  <span style={{ background: "#dbeafe", color: "#1e3a8a", borderRadius: 20, padding: "2px 12px", fontSize: "0.8rem", fontWeight: 700 }}>
                    {new Intl.NumberFormat("en-US").format(total_cantidad)} pzas
                  </span>
                )}
              </div>

              {loading ? (
                <div className="text-center py-4"><Spinner color="primary" /></div>
              ) : items.length === 0 ? (
                <div className="alert alert-info">
                  {rl
                    ? "No hay artículos pendientes de recibir en esta recepción Bodega USA."
                    : "No hay artículos pendientes de recibir desde OC."}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-striped">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>MLM / Parent</th>
                        <th>Nombre</th>
                        <th>Variante</th>
                        {rl
                          ? <th>Factura</th>
                          : <><th>Proveedor</th><th>No. OC</th></>
                        }
                        <th>Pend.</th>
                        <th style={{ width: 100 }}>A Recibir</th>
                        <th>USD/pza</th>
                        <th>T.C.</th>
                        <th style={{ width: 90 }}>Lote</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item._id} style={!item.checked ? { opacity: 0.4 } : {}}>
                          <td className="text-center align-middle">
                            <input type="checkbox" checked={item.checked}
                              onChange={e => updateItem(item._id, "checked", e.target.checked)} />
                          </td>
                          <td className="align-middle">{item.mlm}</td>
                          <td className="align-middle">{item.nombre}</td>
                          <td className="align-middle">
                            {rl ? (
                              // Modo Laredo: variante definida en Laredo, solo lectura
                              <span style={{ fontWeight: 600 }}>
                                {item.varianteNombre || "—"}
                              </span>
                            ) : item.variantesOptions && item.variantesOptions.length > 0 ? (
                              // Modo OC: dropdown para seleccionar
                              <Input bsSize="sm" type="select" value={item.variante}
                                onChange={e => updateItem(item._id, "variante", e.target.value)}>
                                <option value="">Sin variante</option>
                                {item.variantesOptions.map(v => (
                                  <option key={v._id} value={v._id}>{v.nombre}</option>
                                ))}
                              </Input>
                            ) : (
                              <span className="text-muted" style={{ fontSize: "0.8rem" }}>—</span>
                            )}
                          </td>
                          {rl ? (
                            <td className="align-middle">{item.noFactura || "—"}</td>
                          ) : (
                            <>
                              <td className="align-middle">{item.proveedor}</td>
                              <td className="align-middle">
                                <span style={{ fontSize: "0.8rem", color: "#1e3a8a", fontWeight: 600 }}>
                                  #{item.idOrdenCompra}
                                </span>
                              </td>
                            </>
                          )}
                          <td className="align-middle">
                            <strong>{new Intl.NumberFormat("en-US").format(item.pendiente_recibir)}</strong>
                          </td>
                          <td>
                            <Input bsSize="sm" type="number" min="0" max={item.pendiente_recibir}
                              value={item.cantidadRecibir} disabled={!item.checked}
                              onChange={e => updateItem(item._id, "cantidadRecibir", parseFloat(e.target.value) || 0)} />
                          </td>
                          <td className="align-middle">{item.precio}</td>
                          <td className="align-middle">
                            <Input bsSize="sm" type="number" step="0.01" value={item.tipoCambio} disabled={!item.checked}
                              onChange={e => updateItem(item._id, "tipoCambio", parseFloat(e.target.value) || 0)} />
                          </td>
                          <td>
                            <Input bsSize="sm" type="text" value={item.lote} disabled={!item.checked}
                              onChange={e => updateItem(item._id, "lote", e.target.value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-3 mb-4">
              <Button type="submit" color="success" disabled={saving || loading || items.length === 0}>
                {saving ? <><Spinner size="sm" /> Guardando...</> : <><i className="fas fa-save" style={{ marginRight: 6 }} />Guardar Entrada</>}
              </Button>{" "}
              <Button color="danger" href={rl ? "/ListadoRecepcionesLaredo" : "/ListadoOrdenesCompra"}>
                <i className="fas fa-arrow-left" style={{ marginRight: 6 }} />Regresar
              </Button>
            </div>
          </Form>
        </div>
      ) : <p className="text-muted ms-3">Sin acceso.</p>}
    </>
  );
}

export default ComprasCreate;
